import type { ReactNode } from "react";
import { Eye, Info, RefreshCw, Trash2 } from "lucide-react";
import { ActionOverflowMenu, ActionOverflowMenuItem } from "@doculegis/shared";
import type { DocumentStatus } from "../types.js";

export const RAG_DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  completed: "Completed",
  processing: "Processing",
  pending: "Pending",
  failed: "Failed",
};

export type RagDocumentActionCallbacks = {
  onView?: (documentId: string) => void;
  onReprocess?: (documentId: string) => void;
  onDelete?: (documentId: string) => void;
  onStatus?: (documentId: string) => void;
};

export function hasAnyDocumentActions(callbacks: RagDocumentActionCallbacks): boolean {
  return Boolean(
    callbacks.onView || callbacks.onReprocess || callbacks.onDelete || callbacks.onStatus
  );
}

type Props = RagDocumentActionCallbacks & {
  status: DocumentStatus;
  documentId: string;
  /** When false, render null instead of em dash (for card overlay). */
  emptyFallback?: ReactNode;
  menuSize?: "xs" | "sm";
};

export function RagDocumentActions({
  status,
  documentId,
  onView,
  onReprocess,
  onDelete,
  onStatus,
  emptyFallback = <span className="text-slate-500">—</span>,
  menuSize = "sm",
}: Props) {
  const items = [];

  if (onView && status === "completed") {
    items.push(
      <ActionOverflowMenuItem
        key="view"
        icon={Eye}
        label="View"
        onClick={() => onView(documentId)}
      />
    );
  }

  if (onReprocess && (status === "completed" || status === "failed")) {
    items.push(
      <ActionOverflowMenuItem
        key="reprocess"
        icon={RefreshCw}
        label="Reprocess"
        onClick={() => onReprocess(documentId)}
      />
    );
  }

  if (onStatus && (status === "processing" || status === "pending")) {
    items.push(
      <ActionOverflowMenuItem
        key="status"
        icon={Info}
        label="View status"
        onClick={() => onStatus(documentId)}
      />
    );
  }

  if (onDelete) {
    items.push(
      <ActionOverflowMenuItem
        key="delete"
        icon={Trash2}
        label="Delete"
        variant="destructive"
        onClick={() => onDelete(documentId)}
      />
    );
  }

  if (items.length === 0) {
    return emptyFallback;
  }

  return <ActionOverflowMenu size={menuSize}>{items}</ActionOverflowMenu>;
}
